import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useLocalization } from '../../contexts/LocalizationContext';
import advertisementService from '../../services/advertisementService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import {
  AdvertisementStatus, AdvertisementMediaType, AdvertisementActionType,
  TYPE_LABELS, STATUS_LABELS, STATUS_BADGE_CLASS, PLATFORM_LABELS, ACTION_TYPE_LABELS,
  PAGE_NAMES, SLOT_NAMES, labelFor, formatIraqDateTime,
} from './adConstants';

const formatShortDate = (dateStr, isRtl) => {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(isRtl ? 'ar-SA' : 'en-GB', { day: 'numeric', month: 'short' });
};

const StatTile = ({ number, label }) => (
  <div className="stat-card">
    <div className="stat-card-content">
      <div className="stat-details">
        <div className="stat-number">{number}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  </div>
);

const DETAIL_GUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const detailGeneratedNameRegex = new RegExp(`^(advertisement\\s+)?${DETAIL_GUID_PATTERN}(\\s*\\(copy\\))?$`, 'i');

const isGeneratedDetailName = (value) => detailGeneratedNameRegex.test((value || '').trim());

const getDetailDisplayTitle = (ad, isRtl) => (
  (isRtl ? ad.titleAr : ad.titleEn)
  || (isRtl ? ad.titleEn : ad.titleAr)
  || (!isGeneratedDetailName(ad.internalName) ? ad.internalName : null)
  || (isRtl ? 'إعلان بدون عنوان' : 'Untitled advertisement')
);

const getDetailSubtitle = (ad, isRtl) => {
  const internal = ad.internalName?.trim();
  if (internal && !isGeneratedDetailName(internal) && internal !== getDetailDisplayTitle(ad, isRtl)) return internal;
  return labelFor(TYPE_LABELS, ad.type, isRtl);
};

const AdvertisementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isRtl } = useLocalization();

  const [ad, setAd] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setIsLoading(true);
    setError(null);
    Promise.all([advertisementService.getDetails(id), advertisementService.getAnalytics(id)])
      .then(([d, a]) => { setAd(d); setAnalytics(a); })
      .catch((err) => setError(err.response?.data?.message || err.message || (isRtl ? 'تعذر تحميل البيانات' : 'Failed to load data')))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const runAction = async (action) => {
    setBusy(true);
    try {
      await action();
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || (isRtl ? 'حدث خطأ' : 'Something went wrong'));
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading...'} />;

  if (error || !ad) {
    return (
      <div className="page-content">
        <div className="error-alert"><span>{error || (isRtl ? 'الإعلان غير موجود' : 'Advertisement not found')}</span></div>
        <Button variant="secondary" onClick={() => navigate('/advertisements')}>{isRtl ? '← العودة' : '← Back'}</Button>
      </div>
    );
  }

  const mediaSrc = ad.mediaType === AdvertisementMediaType.Image ? (getProductImageUrl(ad.mediaUrl) || ad.mediaUrl) : null;
  const displayTitle = getDetailDisplayTitle(ad, isRtl);
  const chartData = (analytics?.byDate || []).map((d) => ({
    ...d,
    displayDate: formatShortDate(d.dateUtc, isRtl),
  }));

  return (
    <div className="page-content promo-detail-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <Button variant="secondary" onClick={() => navigate('/advertisements')}>{isRtl ? '← العودة' : '← Back'}</Button>
            <div>
              <h1 className="page-title">{displayTitle}</h1>
              <p className="page-description">{getDetailSubtitle(ad, isRtl)}</p>
            </div>
          </div>
          <div className="page-header-actions">
            {ad.status !== AdvertisementStatus.Active && ad.status !== AdvertisementStatus.Scheduled && (
              <Button variant="success" disabled={busy} onClick={() => runAction(() => advertisementService.activate(ad.id))}>
                {isRtl ? 'تفعيل' : 'Activate'}
              </Button>
            )}
            {(ad.status === AdvertisementStatus.Active || ad.status === AdvertisementStatus.Scheduled) && (
              <Button variant="secondary" disabled={busy} onClick={() => runAction(() => advertisementService.disable(ad.id))}>
                {isRtl ? 'تعطيل' : 'Disable'}
              </Button>
            )}
            <Button variant="secondary" onClick={() => navigate(`/advertisements/${ad.id}/edit`)}>
              {isRtl ? 'تعديل' : 'Edit'}
            </Button>
          </div>
        </div>
      </div>

      {/* Analytics stat tiles */}
      <div className="stats-grid mb-6">
        <StatTile number={(analytics?.uniqueViews ?? 0).toLocaleString()} label={isRtl ? 'مشاهدات فريدة' : 'Unique Views'} />
        <StatTile number={(analytics?.totalImpressions ?? 0).toLocaleString()} label={isRtl ? 'إجمالي الظهور' : 'Total Impressions'} />
        <StatTile number={(analytics?.uniqueClicks ?? 0).toLocaleString()} label={isRtl ? 'نقرات فريدة' : 'Unique Clicks'} />
        <StatTile number={(analytics?.totalClicks ?? 0).toLocaleString()} label={isRtl ? 'إجمالي النقرات' : 'Total Clicks'} />
        <StatTile number={`${analytics?.ctr ?? 0}%`} label={isRtl ? 'نسبة النقر إلى الظهور (CTR)' : 'CTR'} />
      </div>

      <div className="dashboard-grid">
        {/* Ad info card */}
        <div className="card card-full-width">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'تفاصيل الإعلان' : 'Advertisement Details'}</h3></div>
          <div className="card-body promo-detail-body">
            <div className="promo-detail-media">
              {mediaSrc ? <img src={mediaSrc} alt="" /> : <div className="promo-detail-media-placeholder">{isRtl ? 'فيديو' : 'Video'}</div>}
            </div>
            <div className="promo-detail-fields">
              <div className="promo-detail-row"><span>{isRtl ? 'الحالة' : 'Status'}</span><span className={`badge ${STATUS_BADGE_CLASS[ad.status]}`}>{labelFor(STATUS_LABELS, ad.status, isRtl)}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'النوع' : 'Type'}</span><span>{labelFor(TYPE_LABELS, ad.type, isRtl)}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'تاريخ البدء' : 'Start Date'}</span><span>{ad.startAtUtc ? formatIraqDateTime(ad.startAtUtc, isRtl) : (isRtl ? 'فوري' : 'Immediate')}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'تاريخ الانتهاء' : 'Expiration Date'}</span><span>{ad.expireAtUtc ? formatIraqDateTime(ad.expireAtUtc, isRtl) : (isRtl ? 'بدون' : 'None')}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'آخر تحديث' : 'Last Updated'}</span><span>{ad.updatedAt ? formatIraqDateTime(ad.updatedAt, isRtl) : formatIraqDateTime(ad.createdAt, isRtl)}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'زر CTA' : 'CTA'}</span><span>{ad.showCta ? ((isRtl ? ad.ctaTextAr : ad.ctaTextEn) || '—') : (isRtl ? 'مخفي' : 'Hidden')}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'الإجراء عند النقر' : 'Click Action'}</span><span>{labelFor(ACTION_TYPE_LABELS, ad.actionType, isRtl)}{ad.actionUrl ? ` — ${ad.actionUrl}` : ''}</span></div>
              <div className="promo-detail-row"><span>{isRtl ? 'قابل للنقر بالكامل' : 'Whole Ad Clickable'}</span><span>{ad.isClickable ? (isRtl ? 'نعم' : 'Yes') : (isRtl ? 'لا' : 'No')}</span></div>
            </div>
          </div>
        </div>

        {/* Placements */}
        <div className="card card-full-width">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'المواضع' : 'Placements'}</h3></div>
          <div className="card-body p-0">
            {ad.placements.length === 0 ? (
              <div className="empty-state"><p className="empty-description">{isRtl ? 'لا توجد مواضع' : 'No placements'}</p></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{isRtl ? 'المنصة' : 'Platform'}</th>
                      <th>{isRtl ? 'الصفحة' : 'Page'}</th>
                      <th>{isRtl ? 'الموضع' : 'Slot'}</th>
                      <th>{isRtl ? 'الترتيب' : 'Order'}</th>
                      <th>{isRtl ? 'الحالة' : 'Status'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ad.placements.map((p) => (
                      <tr key={p.id}>
                        <td>{labelFor(PLATFORM_LABELS, p.platform, isRtl)}</td>
                        <td>{labelFor(PAGE_NAMES, p.page, isRtl)}</td>
                        <td>{p.slot != null ? labelFor(SLOT_NAMES, p.slot, isRtl) : '—'}</td>
                        <td>{p.displayOrder}</td>
                        <td>
                          <span className={`badge ${p.isEnabled ? 'badge-success' : 'badge-neutral'}`}>
                            {p.isEnabled ? (isRtl ? 'مفعل' : 'Enabled') : (isRtl ? 'معطل' : 'Disabled')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Impressions/Clicks over time */}
        <div className="card card-full-width dashboard-chart-card">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'الأداء عبر الزمن' : 'Performance Over Time'}</h3></div>
          <div className="card-body">
            {chartData.length === 0 ? (
              <div className="chart-empty" style={{ height: 260 }}>
                <p>{isRtl ? 'لا توجد بيانات أداء بعد' : 'No performance data yet'}</p>
                <p className="chart-empty-hint">{isRtl ? 'ستظهر الظهور والنقرات هنا حسب التاريخ' : 'Impressions and clicks by date will appear here'}</p>
              </div>
            ) : (
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 8, right: 20, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="displayDate" tick={{ fontSize: 12, fill: 'var(--text-muted)' }} stroke="var(--border)" tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--text-muted)' }} stroke="var(--border)" tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-md)' }}
                      labelStyle={{ color: 'var(--text)' }}
                      formatter={(value, name) => [value, name === 'impressions' ? (isRtl ? 'الظهور' : 'Impressions') : (isRtl ? 'النقرات' : 'Clicks')]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: 12 }}
                      formatter={(value) => (value === 'impressions' ? (isRtl ? 'الظهور' : 'Impressions') : (isRtl ? 'النقرات' : 'Clicks'))}
                    />
                    <Line type="monotone" dataKey="impressions" name="impressions" stroke="var(--primary)" strokeWidth={2} dot={{ fill: 'var(--primary)', r: 3 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="clicks" name="clicks" stroke="var(--secondary)" strokeWidth={2} dot={{ fill: 'var(--secondary)', r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>

        {/* By platform */}
        <div className="card">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'حسب المنصة' : 'By Platform'}</h3></div>
          <div className="card-body p-0">
            {(analytics?.byPlatform || []).length === 0 ? (
              <div className="empty-state"><p className="empty-description">{isRtl ? 'لا توجد بيانات' : 'No data'}</p></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{isRtl ? 'المنصة' : 'Platform'}</th>
                      <th>{isRtl ? 'مشاهدات فريدة' : 'Unique Views'}</th>
                      <th>{isRtl ? 'الظهور' : 'Impressions'}</th>
                      <th>{isRtl ? 'نقرات فريدة' : 'Unique Clicks'}</th>
                      <th>{isRtl ? 'النقرات' : 'Clicks'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.byPlatform || []).map((row) => (
                      <tr key={row.platform}>
                        <td>{labelFor(PLATFORM_LABELS, row.platform, isRtl)}</td>
                        <td>{row.uniqueViews.toLocaleString()}</td>
                        <td>{row.totalImpressions.toLocaleString()}</td>
                        <td>{row.uniqueClicks.toLocaleString()}</td>
                        <td>{row.totalClicks.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* By placement */}
        <div className="card">
          <div className="card-header"><h3 className="card-title">{isRtl ? 'حسب الموضع' : 'By Placement'}</h3></div>
          <div className="card-body p-0">
            {(analytics?.byPlacement || []).length === 0 ? (
              <div className="empty-state"><p className="empty-description">{isRtl ? 'لا توجد بيانات' : 'No data'}</p></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{isRtl ? 'الصفحة' : 'Page'}</th>
                      <th>{isRtl ? 'الموضع' : 'Slot'}</th>
                      <th>{isRtl ? 'الظهور' : 'Impressions'}</th>
                      <th>{isRtl ? 'النقرات' : 'Clicks'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics?.byPlacement || []).map((row) => (
                      <tr key={row.placementId}>
                        <td>{labelFor(PAGE_NAMES, row.page, isRtl)}</td>
                        <td>{row.slot != null ? labelFor(SLOT_NAMES, row.slot, isRtl) : '—'}</td>
                        <td>{row.totalImpressions.toLocaleString()}</td>
                        <td>{row.totalClicks.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .promo-detail-body { display: flex; gap: var(--sp-6); flex-wrap: wrap; }
        .promo-detail-media {
          width: 220px; height: 220px; flex-shrink: 0;
          border-radius: var(--r-md); overflow: hidden; background: var(--neutral-100);
          display: flex; align-items: center; justify-content: center;
        }
        .promo-detail-media img { width: 100%; height: 100%; object-fit: cover; }
        .promo-detail-media-placeholder { color: var(--text-muted); font-size: var(--fs-sm); }
        .promo-detail-fields { flex: 1; min-width: 260px; display: flex; flex-direction: column; gap: var(--sp-2); }
        .promo-detail-row {
          display: flex; justify-content: space-between; gap: var(--sp-4);
          padding: var(--sp-2) 0; border-bottom: 1px solid var(--border-light);
          font-size: var(--fs-sm);
        }
        .promo-detail-row span:first-child { color: var(--text-muted); }
        .promo-detail-row span:last-child { color: var(--text); font-weight: 500; text-align: end; }
      `}</style>
    </div>
  );
};

export default AdvertisementDetail;
